import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Scale, AlertTriangle, Handshake, Gavel, RefreshCw } from 'lucide-react';

export default function TermsConditions({ onGoHome }) {
  const sections = [
    {
      icon: FileText,
      title: "Acceptance of Terms",
      content: `By accessing and using the Done Commercials website and services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.`
    },
    {
      icon: Handshake,
      title: "Services Description",
      content: `Done Commercials provides commercial mortgage brokerage services, connecting clients with lenders for commercial property financing. Our services include consultation, application processing, and guidance throughout the mortgage process. We act as intermediaries between borrowers and lenders and do not provide direct lending services.`
    },
    {
      icon: Scale,
      title: "User Responsibilities",
      content: `You agree to provide accurate, current, and complete information during the application process. You are responsible for maintaining the confidentiality of any account information and for all activities under your account. You agree not to use our services for any unlawful purpose or in violation of any applicable laws or regulations.`
    },
    {
      icon: AlertTriangle,
      title: "Disclaimer of Warranties",
      content: `Our services are provided "as is" without warranties of any kind, either express or implied. We do not guarantee approval of any mortgage application or specific terms from lenders. Market conditions, lender policies, and individual circumstances affect all financing decisions. Past performance or quoted rates do not guarantee future results.`
    },
    {
      icon: Gavel,
      title: "Limitation of Liability",
      content: `Done Commercials shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability shall not exceed the fees paid for our services. We are not responsible for lender decisions, market fluctuations, or circumstances beyond our control.`
    },
    {
      icon: RefreshCw,
      title: "Modifications to Terms",
      content: `We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services after any modifications indicates your acceptance of the updated terms. We encourage you to review these terms periodically.`
    }
  ];

  const additionalTerms = [
    {
      title: "Intellectual Property",
      content: "All content on this website, including text, graphics, logos, and images, is the property of Done Commercials and is protected by Canadian and international copyright laws. You may not reproduce, distribute, or create derivative works without our express written permission."
    },
    {
      title: "Privacy",
      content: "Your use of our services is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect, use, and protect your personal information."
    },
    {
      title: "Third-Party Links",
      content: "Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of any third-party sites. We encourage you to review the terms and privacy policies of any third-party sites you visit."
    },
    {
      title: "Governing Law",
      content: "These Terms and Conditions are governed by and construed in accordance with the laws of the Province of Ontario, Canada. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Ontario."
    },
    {
      title: "Severability",
      content: "If any provision of these Terms and Conditions is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect."
    },
    {
      title: "Contact Information",
      content: "For questions about these Terms and Conditions, please contact us at info@donecommercial.ca."
    }
  ];

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="bg-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={onGoHome}
            className="inline-flex items-center text-emerald-400 hover:text-emerald-300 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </button>
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Terms & Conditions
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Last updated: December 2024
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          className="prose prose-lg max-w-none"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-slate-600 text-lg leading-relaxed mb-12">
            Welcome to Done Commercials. These Terms and Conditions govern your use of our website 
            and commercial mortgage brokerage services. Please read these terms carefully before 
            using our services.
          </p>

          {/* Main Sections with Icons */}
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              className="mb-8 bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1 }}
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4">
                  <section.icon className="w-6 h-6 text-emerald-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{section.title}</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">{section.content}</p>
            </motion.div>
          ))}

          {/* Additional Terms */}
          <motion.div
            className="mt-16"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Additional Terms</h2>
            <div className="space-y-6">
              {additionalTerms.map((term, index) => (
                <div 
                  key={term.title}
                  className="border-l-4 border-emerald-500 pl-6 py-2"
                >
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{term.title}</h3>
                  <p className="text-slate-600">{term.content}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Agreement Box */}
          <motion.div
            className="mt-16 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-white"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-bold mb-4">Agreement</h2>
            <p className="text-slate-300 mb-6">
              By using Done Commercials' website and services, you acknowledge that you have read 
              and understood these Terms and Conditions and agree to be bound by them. If you have 
              any questions, please contact us before using our services.
            </p>
            <button 
              onClick={onGoHome}
              className="inline-flex items-center bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition-colors"
            >
              Back to Home
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            &copy; 2025 Done Commercials. All rights reserved.
          </p>
          <div className="mt-4 space-x-6">
            <a href="#privacy-policy" className="text-slate-400 hover:text-emerald-400">Privacy Policy</a>
            <a href="#terms-conditions" className="text-emerald-400 hover:text-emerald-300">Terms & Conditions</a>
          </div>
        </div>
      </footer>
    </motion.div>
  );
}
